import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Check, X, MailCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Supported markets. Timezone list mirrors backend SUPPORTED_TIMEZONES.
const COUNTRIES = {
  NZ: {
    label: "New Zealand",
    tax: "GST 15% · NZD",
    zones: [
      ["Pacific/Auckland", "New Zealand (Auckland / Wellington / Nelson)"],
      ["Pacific/Chatham", "Chatham Islands"],
    ],
  },
  AU: {
    label: "Australia",
    tax: "GST 10% · AUD",
    zones: [
      ["Australia/Sydney", "Sydney / Canberra (NSW, ACT)"],
      ["Australia/Melbourne", "Melbourne (VIC)"],
      ["Australia/Brisbane", "Brisbane (QLD)"],
      ["Australia/Adelaide", "Adelaide (SA)"],
      ["Australia/Perth", "Perth (WA)"],
      ["Australia/Hobart", "Hobart (TAS)"],
      ["Australia/Darwin", "Darwin (NT)"],
    ],
  },
};

// Mirrors backend signup_guard.validate_password so the rules are visible
// as they type instead of arriving as a server error after submit.
const PW_MIN = 10;

function passwordChecks(pw, email) {
  const local = (email || "").split("@")[0].trim().toLowerCase();
  return [
    { id: "len", label: `At least ${PW_MIN} characters`, ok: pw.length >= PW_MIN },
    {
      id: "mix",
      label: "A letter and a number",
      ok: /[A-Za-z]/.test(pw) && /\d/.test(pw),
    },
    {
      id: "email",
      label: "Doesn't contain your email",
      ok: !(local.length >= 4 && pw.toLowerCase().includes(local)),
    },
  ];
}

export default function SignupPage() {
  const [form, setForm] = useState({
    display_name: "",
    slug: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
    country: "NZ",
    timezone: "Pacific/Auckland",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);
  const [resending, setResending] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickCountry = (code) =>
    setForm((f) => ({ ...f, country: code, timezone: COUNTRIES[code].zones[0][0] }));

  const autoSlug = (name) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 40);

  const checks = useMemo(
    () => passwordChecks(form.admin_password, form.admin_email),
    [form.admin_password, form.admin_email],
  );
  const pwOk = checks.every((c) => c.ok);
  const mismatch = confirmPassword.length > 0 && confirmPassword !== form.admin_password;

  const submit = async (e) => {
    e.preventDefault();
    if (!pwOk) {
      toast.error("Please choose a stronger password");
      return;
    }
    if (form.admin_password !== confirmPassword) {
      toast.error("The two passwords don't match");
      return;
    }
    setSubmitting(true);
    setError(null);
    const payload = { ...form, slug: form.slug || autoSlug(form.display_name) };
    try {
      const r = await fetch(`${API}/platform/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      let body = {};
      try {
        body = await r.json();
      } catch {
        body = {};
      }
      if (!r.ok) {
        // A 500 carries no useful `detail`, so the old code showed a toast
        // saying "Signup failed" at best and the page looked frozen —
        // reported as "everything grinds to a halt" (Jun 2026). Keep the
        // message on screen until they dismiss it, and say what to do next.
        const serverDetail =
          typeof body.detail === "string" ? body.detail.trim() : "";
        // "Internal Server Error" is FastAPI boilerplate — it tells the
        // operator nothing, so treat it as no message at all.
        const useful =
          serverDetail && serverDetail.toLowerCase() !== "internal server error";
        const detail = useful
          ? serverDetail
          : r.status >= 500
            ? "Something broke on our side while creating your company. Nothing was saved and you can retry."
            : `Signup failed (error ${r.status}).`;
        setError({ status: r.status, detail });
        toast.error(detail);
        return;
      }
      setError(null);
      // Nothing is created yet — the tenant database is only provisioned
      // once they click the link, which is what keeps bots out and catches
      // a mistyped address before it strands them.
      setSent({
        email: body.login_email || form.admin_email,
        emailSent: body.email_sent !== false,
        hours: body.expires_hours || 24,
      });
    } catch (err) {
      toast.error("Network error — please retry");
      setError({
        status: 0,
        detail: "Couldn't reach the server. Check your connection and retry.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await fetch(`${API}/platform/signup/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: sent.email }),
      });
      toast.success("Confirmation email sent again");
    } catch {
      toast.error("Couldn't resend — please retry shortly");
    } finally {
      setResending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-950 px-4"
        data-testid="signup-sent-panel"
      >
        <Card className="w-full max-w-md border-slate-800 bg-slate-900 text-slate-100">
          <CardHeader>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/15 mb-3">
              <MailCheck className="h-6 w-6 text-sky-400" />
            </div>
            <CardTitle className="text-2xl">Check your inbox</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-300">
              We've sent a confirmation link to{" "}
              <span
                className="font-semibold text-slate-100"
                data-testid="signup-sent-email"
              >
                {sent.email}
              </span>
              . Click it to finish creating your company.
            </p>
            <p className="text-sm text-slate-400">
              Your company isn't created until you confirm, so nothing is lost if
              you got the address wrong — just sign up again with the right one.
              The link lasts {sent.hours} hours.
            </p>
            {!sent.emailSent && (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200"
                data-testid="signup-email-warning"
                role="alert"
              >
                We couldn't send the email just now. Use Resend below, and if it
                still doesn't arrive, contact support.
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={resend}
                disabled={resending}
                data-testid="signup-resend-btn"
                className="flex-1"
              >
                {resending ? "Sending…" : "Resend email"}
              </Button>
              <Button asChild className="flex-1" data-testid="signup-to-login-btn">
                <Link to="/login">Go to login</Link>
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center">
              Wrong address?{" "}
              <button
                type="button"
                onClick={() => setSent(null)}
                className="text-sky-400 hover:underline"
                data-testid="signup-start-over-btn"
              >
                Start over
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-8"
      data-testid="signup-page"
    >
      <Card className="w-full max-w-md border-slate-800 bg-slate-900 text-slate-100">
        <CardHeader>
          <CardTitle className="text-2xl">Create your Tow-Hook account</CardTitle>
          <p className="text-sm text-slate-400 mt-1">
            Get your dispatch system up and running in under a minute.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {error && (
              <div
                className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200"
                data-testid="signup-error"
                role="alert"
              >
                <p className="font-semibold">We couldn't create your company</p>
                <p className="mt-0.5 text-red-200/90">{error.detail}</p>
                {error.status >= 500 && (
                  <p className="mt-1 text-[12px] text-red-200/70">
                    If it keeps happening, email support and quote error{" "}
                    {error.status}.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="display_name">Company name</Label>
              <Input
                id="display_name"
                data-testid="signup-company-name"
                value={form.display_name}
                onChange={upd("display_name")}
                required
                placeholder="Smith Towing Ltd"
              />
            </div>
            <div>
              <Label htmlFor="slug">
                URL slug{" "}
                <span className="text-xs text-slate-500">
                  (leave blank to auto-generate)
                </span>
              </Label>
              <Input
                id="slug"
                data-testid="signup-slug"
                value={form.slug}
                onChange={upd("slug")}
                placeholder={autoSlug(form.display_name) || "smith-towing"}
              />
            </div>
            <div>
              <Label htmlFor="admin_name">Your name</Label>
              <Input
                id="admin_name"
                data-testid="signup-admin-name"
                value={form.admin_name}
                onChange={upd("admin_name")}
                required
                placeholder="Bob Smith"
              />
            </div>
            <div>
              <Label htmlFor="admin_email">Login email</Label>
              <Input
                id="admin_email"
                data-testid="signup-admin-email"
                type="email"
                value={form.admin_email}
                onChange={upd("admin_email")}
                required
                placeholder="you@company.co.nz"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                We'll send a confirmation link here — make sure it's one you can
                open.
              </p>
            </div>

            <div>
              <Label htmlFor="admin_password">Password</Label>
              <div className="relative">
                <Input
                  id="admin_password"
                  data-testid="signup-admin-password"
                  type={showPw ? "text" : "password"}
                  value={form.admin_password}
                  onChange={upd("admin_password")}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="signup-password-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {form.admin_password.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="signup-password-rules">
                  {checks.map((c) => (
                    <li
                      key={c.id}
                      className={
                        "flex items-center gap-1.5 text-[11px] " +
                        (c.ok ? "text-emerald-400" : "text-slate-500")
                      }
                      data-testid={`signup-pw-rule-${c.id}`}
                    >
                      {c.ok ? (
                        <Check className="h-3 w-3 shrink-0" />
                      ) : (
                        <X className="h-3 w-3 shrink-0" />
                      )}
                      {c.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <Label htmlFor="confirm_password">Repeat password</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  data-testid="signup-confirm-password"
                  type={showPw2 ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw2((v) => !v)}
                  data-testid="signup-confirm-toggle"
                  aria-label={showPw2 ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                >
                  {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {mismatch && (
                <p
                  className="mt-1 text-[11px] text-red-300"
                  data-testid="signup-password-mismatch"
                >
                  The two passwords don't match.
                </p>
              )}
            </div>

            <div>
              <Label>Where do you operate?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {Object.entries(COUNTRIES).map(([code, c]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => pickCountry(code)}
                    data-testid={`signup-country-${code.toLowerCase()}`}
                    className={
                      "rounded-md border px-3 py-2 text-left transition-colors " +
                      (form.country === code
                        ? "border-sky-500 bg-sky-500/10 text-slate-100"
                        : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500")
                    }
                  >
                    <div className="text-sm font-semibold">{c.label}</div>
                    <div className="text-[11px] text-slate-400">{c.tax}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="timezone">Your timezone</Label>
              <select
                id="timezone"
                data-testid="signup-timezone"
                value={form.timezone}
                onChange={upd("timezone")}
                className="w-full rounded-md bg-slate-800 border border-slate-700 text-slate-100 h-10 px-3 text-sm"
              >
                {COUNTRIES[form.country].zones.map(([z, label]) => (
                  <option key={z} value={z}>{label}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                Every date, after-hours rate and storage day is calculated in this
                timezone — not your device's.
              </p>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="signup-submit-btn"
              className="w-full"
            >
              {submitting ? "Sending…" : "Create my company"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-sky-400 hover:underline">
                Log in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
